import "./style.css";
import "./theme-recife.css";
import "./controls.css";
import "./solo.css";
import "./countdown.css";
import "./home-demo.css";
import "./profile.css";
import "./panels.css";
import "./lobby.css";
import "./card-preview.css";
import "./collections.css";
import "./round-feedback.css";
import "@fontsource-variable/dm-sans";
import "@fontsource-variable/manrope";
import { io, type Socket } from "socket.io-client";
import {
  generateDeck,
  getDeckStats,
  shuffledDeck,
  type DeckOrder,
  type GameCard,
} from "../shared/deck";
import { getPrintPlan } from "../shared/print-plan";
import { getDuplexSlots } from "../shared/print-plan";
import { DEFAULT_CARD_BACK, collectionSymbolCount, newCollectionId, toPlayableCollection, type CardCollection } from "../shared/collections";
import { collectionStore, hydrateCollections } from "./collections-store";
import { refreshInstalledClassic } from "./classic-refresh";
import { createCollectionLibrary } from "./collection-library";
import { createCardBack } from "./card-back";
import { uploadRoomCollection, loadRoomCollection } from "./room-collection";
import { START_COUNTDOWN_MS } from "../shared/countdown";
import { createRoundCountdown } from "./countdown";
import {
  PRACTICE_LENGTHS,
  claimPractice,
  createPractice,
  loadPracticeLength,
  practiceTarget,
  savePracticeLength,
  type PracticeGame,
  type PracticeLength,
} from "./practice";
import type {
  Ack,
  PublicRoom,
  Session,
  ServerToClientEvents,
  ClientToServerEvents,
} from "../shared/protocol";
import { RoundFeedback } from "./round-feedback";
import { createCard } from "./card";
import { createCardPreview } from "./card-preview";
import { createHomeDemo, type HomeDemo } from "./home-demo";
import { createTutorial, type Tutorial } from "./tutorial";
import { achievementArt } from "./badges";
import { uiArt } from "./art";
import { invitationUrl, renderInvitationQr } from "./invitations";
import { generateNickname, loadNickname, rememberNickname } from "./nickname";
import { getShell, icon, escape, stack, avatar } from "./ui";
import {
  t,
  getLocale,
  setLocale,
  serverMessage,
  intlNumber,
  type Locale,
} from "./i18n";
import {
  initEffects,
  isSoundEnabled,
  toggleSound,
  celebrateMatch,
  celebrateVictory,
} from "./effects";
import {
  loadProgress,
  saveProgress,
  levelInfo,
  recordHit,
  recordVictory,
  recordPracticeComplete,
  badges,
  type ProgressReward,
} from "./progress";

await hydrateCollections();
const app = document.querySelector<HTMLDivElement>("#app")!;
app.innerHTML = getShell();
const tr = (key: string, vars?: Record<string, string | number>) =>
  escape(t(key, vars));
const $ = <T extends HTMLElement = HTMLElement>(selector: string) =>
  document.querySelector<T>(selector)!;
const socket: Socket<ServerToClientEvents, ClientToServerEvents> = io({
  reconnection: true,
  timeout: 8000,
});
type View = "home" | "studio" | "rules" | "room" | "practice" | "collections";
let view: View = "home";
let homeDemo: HomeDemo | undefined;
let tutorial: Tutorial | undefined;
let online = false;
let room: PublicRoom | null = null;
let session: Session | null = null;
let actionPending = false;
let profile = loadProgress();
let nickname = loadNickname(getLocale());
let streak = 0;
let bestRoundStreak = 0;
let roundXp = 0;
let shownAt = performance.now();
let reactions: number[] = [];
let printOrder: DeckOrder = activeCollection()?.order ?? 7;
let cardsPerPage: 1 | 2 | 4 = 4;
let printSizeMode: "original" | "large" = "original";
let previewPage = 0;
let enlargedCardIndex = 0;
let cardPreview: ReturnType<typeof createCardPreview>;
let library: ReturnType<typeof createCollectionLibrary> | undefined;
let printSides: "front" | "duplex" | "back" = "duplex";
let printFace: "front" | "back" = "front";
let practiceCollection: CardCollection | null = null;
let roomCollection: CardCollection | null = null;
let roomCollectionId: string | null = null;
let roomCollectionLoading = false;
let roomCollectionError = "";
let roomReadyPending = false;
let roomReadyEpoch = 0;
let exporting = false;
let studioDirty = true;
let roomDirty = true;
let practiceDirty = true;
let profileDirty = true;
let frame: number | null = null;
let toastTimer: ReturnType<typeof setTimeout>;
let announcementTimer: ReturnType<typeof setTimeout>;
let announcedFeedback = 0;
const roundFeedback = new RoundFeedback(() => renderRoundFeedback());
let practice: PracticeGame | null = null;
let practiceLength = loadPracticeLength();
let lastCenterId: number | null = null;
let lastOwnId: number | null = null;
let practiceStartsAt = 0;
let practiceRound = 0;
let roomStartsAt = 0;
const countdown = createRoundCountdown(() => {
  shownAt = performance.now();
  roomDirty = true;
  practiceDirty = true;
  scheduleRender();
});

function isRoundActive() {
  return (view === "room" && room?.status === "playing") ||
    (view === "practice" && !!practice?.own);
}
function toast(message: string, error = false) {
  const translated = serverMessage(message);
  if (isRoundActive()) {
    roundFeedback.show({
      kind: error ? "error" : "notice",
      label: error ? t("RECONNEXION…") : translated,
      announcement: translated,
    });
    return;
  }
  clearTimeout(toastTimer);
  const el = $("#toast");
  el.textContent = translated;
  el.classList.toggle("error", error);
  el.hidden = false;
  toastTimer = setTimeout(() => {
    el.hidden = true;
  }, 3500);
}
function renderRoundFeedback() {
  const event = isRoundActive() ? roundFeedback.current : null;
  for (const status of document.querySelectorAll<HTMLElement>("[data-round-status]")) {
    status.textContent = event?.label ?? "";
    status.hidden = !event?.label;
    status.parentElement?.classList.toggle("has-round-notice", !!event?.label);
    status.parentElement?.classList.toggle("has-round-error", event?.kind === "error");
  }
  for (const counter of document.querySelectorAll<HTMLElement>("[data-round-score], [data-player-count]")) {
    const isPlayer = event?.kind === "opponent"
      ? counter.closest<HTMLElement>("[data-player-id]")?.dataset.playerId === event.playerId
      : counter.hasAttribute("data-round-score");
    counter.classList.toggle("round-pulse", !!event && isPlayer && event.kind !== "notice");
    if (event && isPlayer) counter.dataset.roundFeedback = event.kind;
    else delete counter.dataset.roundFeedback;
  }
  const announcement = $("#round-announcement");
  if (!event) {
    clearTimeout(announcementTimer);
    announcement.textContent = "";
  } else if (event.id !== announcedFeedback) {
    announcedFeedback = event.id;
    clearTimeout(announcementTimer);
    announcement.textContent = "";
    // Repeated short errors remain spoken without delaying or locking a turn.
    announcementTimer = setTimeout(() => {
      if (isRoundActive() && roundFeedback.current?.id === event.id)
        announcement.textContent = event.announcement;
    }, 30);
  }
}
function scheduleRender() {
  if (frame !== null) return;
  frame = requestAnimationFrame(() => {
    frame = null;
    render();
  });
}
function setView(next: View) {
  if (next !== view) countdown.cancel();
  view = next === "home" && session && room ? "room" : next;
  roundFeedback.clear();
  scheduleRender();
  window.scrollTo({ top: 0, behavior: "instant" });
}
function closeDialogs() {
  for (const dialog of document.querySelectorAll<HTMLDialogElement>(
    "dialog[open]",
  ))
    dialog.close();
}
function openDialog(kind: "create" | "join") {
  if (session) {
    setView("room");
    return;
  }
  $<HTMLDialogElement>(`#${kind}-dialog`).showModal();
  const input = $<HTMLInputElement>(`#${kind}-name`);
  if (!input.value.trim()) input.value = nickname;
  input.focus();
  input.select();
}
function setNickname(name: string) {
  if (!name.trim()) return;
  nickname = name.trim().slice(0, 20);
  rememberNickname(nickname);
  for (const id of ["create-name", "join-name"])
    $<HTMLInputElement>(`#${id}`).value = nickname;
  profileDirty = true;
  scheduleRender();
}
function activeCollection(): CardCollection | null {
  const state = collectionStore.getState();
  const game = state.collections.find(game => game.id === state.selectedId);
  return game && game.symbols.length === collectionSymbolCount(game.order) ? game : null;
}
function refreshCollectionControls() {
  const state = collectionStore.getState();
  const selected = activeCollection();
  const options = `<option value="">Náutico / Recife</option>` + state.collections
    .filter(game => game.symbols.length === collectionSymbolCount(game.order))
    .map(game => `<option value="${escape(game.id)}">${escape(game.name)}</option>`).join("");
  for (const select of document.querySelectorAll<HTMLSelectElement>("[data-collection-select]")) {
    select.innerHTML = options;
    select.value = selected?.id ?? "";
    select.disabled = exporting || (select.id === "game-collection" && actionPending);
  }
  $("#selected-game").innerHTML = `${icon("stack", 22)}<span><small>${tr("Jeu sélectionné")}</small><strong>${escape(selected?.name ?? "Náutico / Recife")}</strong></span>${icon("arrow", 18)}`;
  const gameOrder = $<HTMLSelectElement>("#game-order");
  const printOrderSelect = $<HTMLSelectElement>("#print-order");
  gameOrder.disabled = !!selected || actionPending;
  printOrderSelect.disabled = !!selected || exporting;
  if (selected) gameOrder.value = String(selected.order);
  printOrderSelect.value = String(printOrder);
}
function confirmRoomCollectionReady(next: PublicRoom) {
  if (!online || !session || !roomCollection || roomReadyPending || next.players.find(player => player.id === session?.playerId)?.assetsReady !== false) return;
  const epoch = ++roomReadyEpoch;
  const code = next.code;
  const playerId = session.playerId;
  roomReadyPending = true;
  socket.timeout(8000).emit("room:ready", {}, (error, result) => {
    if (epoch !== roomReadyEpoch || room?.code !== code || session?.playerId !== playerId) return;
    roomReadyPending = false;
    if (!error && result?.ok) acceptRoom(result.data);
    else {
      roomCollectionError = error ? t("Connexion interrompue. Réessaie.") : !result?.ok ? result.error : "";
      roomDirty = true;
      scheduleRender();
    }
  });
}
function ensureRoomCollection(next: PublicRoom) {
  const id = next.collection?.id ?? null;
  if (id === roomCollectionId) { if (id) confirmRoomCollectionReady(next); return; }
  roomCollectionId = id;
  roomCollection = null;
  roomCollectionError = "";
  roomCollectionLoading = !!id;
  if (!id) return;
  void loadRoomCollection(next).then(collection => {
    if (room?.code !== next.code || room.collection?.id !== id) return;
    roomCollection = collection;
    roomCollectionLoading = false;
    roomDirty = true;
    scheduleRender();
    confirmRoomCollectionReady(room);
  }).catch(error => {
    if (room?.code !== next.code || room.collection?.id !== id) return;
    roomCollectionLoading = false;
    roomCollectionError = error instanceof Error ? error.message : t("Impossible de charger les images du salon. Vérifie ta connexion et réessaie.");
    roomDirty = true;
    scheduleRender();
  });
}
function saveSession(next: Session | null) {
  session = next;
  try {
    if (next)
      sessionStorage.setItem(
        "pile-session",
        JSON.stringify({ code: next.room.code, token: next.token }),
      );
    else sessionStorage.removeItem("pile-session");
  } catch {
    /* Storage optional. */
  }
}
function resetRound() {
  roundFeedback.clear();
  streak = 0;
  bestRoundStreak = 0;
  roundXp = 0;
  reactions = [];
  shownAt = performance.now();
  lastCenterId = null;
  lastOwnId = null;
}
function applyReward(result: ProgressReward) {
  profile = result.progress;
  saveProgress(profile);
  profileDirty = true;
  roundXp += result.gained;
  const levelBefore = levelInfo(result.progress.xp - result.gained).level;
  const levelAfter = levelInfo(result.progress.xp).level;
  if (levelAfter > levelBefore && view !== "practice" && !isRoundActive())
    toast(
      t("NIVEAU {level} DÉBLOQUÉ · Tes réflexes montent d’un cran !", {
        level: levelAfter,
      }),
    );
  if (result.unlocked.length && view !== "practice" && !isRoundActive()) {
    const names = badges(profile)
      .filter((b) => result.unlocked.includes(b.id))
      .map((b) => t(b.name))
      .join(" + ");
    toast(t("BADGE DÉBLOQUÉ · {names}", { names }));
  }
  return result.gained;
}
function currentProgress() {
  const stored = loadProgress();
  return stored.xp >= profile.xp ? stored : profile;
}
function rewardHit() {
  streak++;
  bestRoundStreak = Math.max(bestRoundStreak, streak);
  reactions.push(Math.max(1, performance.now() - shownAt));
  const gained = applyReward(recordHit(currentProgress(), streak));
  celebrateMatch({
    from: document.querySelector<HTMLElement>(
      "#own-card-slot .game-card, #practice-own .game-card",
    ),
    to: document.querySelector<HTMLElement>("#center-card-slot, #practice-center"),
    streak,
  });
  roundFeedback.show({
    kind: "match",
    announcement: t("CARTE POSÉE · +{xp} XP", { xp: gained }),
    playerId: session?.playerId,
  });
  return gained;
}
function acceptRoom(next: PublicRoom) {
  if (room?.code === next.code && next.revision < room.revision) return;
  const previous = room?.code === next.code ? room : null;
  // Anchor the server deadline to a monotonic local clock, including on resume.
  if (next.startsAt !== previous?.startsAt || !previous)
    roomStartsAt = performance.now() + Math.max(0, (next.startsAt ?? next.serverNow) - next.serverNow);
  if (next.status === "playing" && previous?.status !== "playing") resetRound();
  const previousScore = previous?.players.find(
    (p) => p.id === session?.playerId,
  )?.score;
  const nextScore = next.players.find((p) => p.id === session?.playerId)?.score;
  // Only increases in authoritative room snapshots grant XP. Acks and broadcasts
  // can both deliver one revision, so neither event directly grants rewards.
  if (
    previousScore !== undefined &&
    nextScore !== undefined &&
    nextScore > previousScore
  ) {
    for (let i = previousScore; i < nextScore; i++) rewardHit();
  }
  if (next.centerCard?.id !== previous?.centerCard?.id)
    shownAt = performance.now();
  if (next.status === "finished" && previous?.status === "playing") {
    if (next.winners.includes(session?.playerId ?? ""))
      applyReward(recordVictory(currentProgress()));
    celebrateVictory();
  }
  room = next;
  ensureRoomCollection(next);
  roomDirty = true;
  scheduleRender();
}
function adopt(next: Session) {
  saveSession(next);
  closeDialogs();
  acceptRoom(next.room);
  setView("room");
}
function setBusy(busy: boolean) {
  actionPending = busy;
  for (const button of document.querySelectorAll<HTMLButtonElement>(
    '#create-form [type="submit"], #join-form [type="submit"]',
  ))
    button.disabled = busy;
  refreshCollectionControls();
  scheduleRender();
}
async function submitRoom(kind: "create" | "join") {
  if (session) {
    closeDialogs();
    setView("room");
    return;
  }
  if (!online) {
    toast("Le serveur se reconnecte. Réessaie dans un instant.", true);
    return;
  }
  if (actionPending) return;
  const name = $<HTMLInputElement>(`#${kind}-name`).value.trim();
  if (!name) return;
  setNickname(name);
  setBusy(true);
  const selected = activeCollection();
  const order = selected?.order ?? Number($<HTMLSelectElement>("#game-order").value) as DeckOrder;
  let collectionId: string | undefined;
  if (kind === "create" && selected) {
    $("#create-status").textContent = t("Envoi des images…");
    try { collectionId = await uploadRoomCollection(selected); }
    catch (error) {
      setBusy(false);
      $("#create-status").textContent = "";
      toast(error instanceof Error ? error.message : "Impossible d’envoyer ce jeu. Vérifie ta connexion et réessaie.", true);
      return;
    }
  }
  const done = (err: Error | null, result: Ack<Session>) => {
    setBusy(false);
    $("#create-status").textContent = "";
    if (err) {
      toast("Le serveur met trop de temps à répondre.", true);
      return;
    }
    if (!result.ok) {
      toast(result.error, true);
      return;
    }
    adopt(result.data);
  };
  if (kind === "create")
    socket
      .timeout(8000)
      .emit(
        "room:create",
        {
          name,
          order,
          ...(collectionId ? { collectionId } : {}),
        },
        done,
      );
  else
    socket
      .timeout(8000)
      .emit(
        "room:join",
        {
          name,
          code: $<HTMLInputElement>("#join-code").value.trim().toUpperCase(),
        },
        done,
      );
}
function roomAction(event: "game:start" | "game:restart") {
  if (!online || actionPending) return;
  setBusy(true);
  socket.timeout(8000).emit(event, {}, (err, result) => {
    setBusy(false);
    if (err) toast("Connexion interrompue. Réessaie.", true);
    else if (!result.ok) toast(result.error, true);
    else acceptRoom(result.data);
  });
}
function claim(symbolId: number, revision: number) {
  if (
    view !== "room" ||
    !online ||
    actionPending ||
    room?.status !== "playing" ||
    performance.now() < roomStartsAt ||
    revision !== room.revision
  )
    return;
  const own = room.players.find((p) => p.id === session?.playerId)?.card;
  if (
    !own?.symbols.includes(symbolId) ||
    !room.centerCard?.symbols.includes(symbolId)
  ) {
    return;
  }
  setBusy(true);
  socket
    .timeout(8000)
    .emit("game:claim", { symbolId, revision }, (err, result) => {
      setBusy(false);
      if (err) toast("Ton clic n’a pas pu être confirmé. Reconnexion…", true);
      else if (result.ok) acceptRoom(result.data);
      else if (!["Ce symbole n’est pas commun aux deux cartes.", "La carte vient de changer. Regardez la nouvelle carte."].includes(result.error))
        toast(result.error, true);
    });
}

socket.on("connect", () => {
  online = true;
  roomDirty = true;
  scheduleRender();
  let saved: { code: string; token: string } | null = null;
  try {
    saved = JSON.parse(sessionStorage.getItem("pile-session") ?? "null");
  } catch {
    /* Invalid storage. */
  }
  if (saved?.code && saved?.token)
    socket
      .timeout(8000)
      .emit(
        "room:resume",
        { code: saved.code, token: saved.token },
        (err, result) => {
          if (err) {
            toast("Impossible de reprendre la partie pour le moment.", true);
            return;
          }
          if (!result.ok) {
            saveSession(null);
            room = null;
            setView("home");
            toast(result.error, true);
          } else adopt(result.data);
        },
      );
});
socket.on("disconnect", (reason) => {
  online = false;
  roomReadyEpoch++;
  roomReadyPending = false;
  roomDirty = true;
  setBusy(false);
  if (reason === "io server disconnect")
    setTimeout(() => socket.connect(), 500);
});
socket.on("connect_error", () => {
  online = false;
  scheduleRender();
});
socket.on("room:state", acceptRoom);
socket.on("game:claimed", ({ playerId }) => {
  if (
    playerId !== session?.playerId &&
    view === "room" &&
    room?.status === "playing"
  ) {
    const name =
      room?.players.find((p) => p.id === playerId)?.name ?? t("Un joueur");
    roundFeedback.show({
      kind: "opponent",
      playerId,
      announcement: t("{name} A TROUVÉ !", { name }),
    });
  }
});
socket.on("room:closed", ({ reason }) => {
  saveSession(null);
  room = null;
  roomCollection = null;
  roomCollectionId = null;
  roomReadyEpoch++;
  roomReadyPending = false;
  closeDialogs();
  setView("home");
  toast(reason);
});

function badgeGlyph(id: string, size = 36) {
  return achievementArt(id, size);
}
function renderProfile() {
  const info = levelInfo(profile.xp);
  $("#level-number").textContent = String(info.level);
  $("#level-label").textContent = String(info.level);
  $("#xp-label").textContent = `${intlNumber(profile.xp)} XP`;
  $("#xp-fill").style.width = `${info.ratio * 100}%`;
  $("#profile-button").setAttribute(
    "aria-label",
    t("Niveau {level}, {xp} XP. Voir mes badges", {
      level: info.level,
      xp: profile.xp,
    }),
  );
  const all = badges(profile);
  const mission = all.find((b) => !b.unlocked) ?? all[all.length - 1];
  $("#home-mission").innerHTML =
    `<span class="mission-icon">${badgeGlyph(mission.id)}</span><div><small>${tr(mission.unlocked ? "COLLECTION COMPLÈTE" : "TON PROCHAIN BADGE")}</small><strong>${tr(mission.name)}</strong></div><span class="mission-progress"><b>${Math.min(mission.current, mission.target)}<small>/${mission.target}</small></b><i><u style="width:${Math.min(1, mission.current / mission.target) * 100}%"></u></i></span>${icon("arrow", 18)}`;
  const unlocked = all.filter((b) => b.unlocked).length;
  $("#profile-content").innerHTML = `
    <header class="profile-heading"><span class="profile-caption">${icon("mark", 18)} ${tr("PALMARÈS")}</span><h2 id="profile-title">${escape(nickname)}</h2></header>
    <section class="player-level" aria-label="${tr("NIVEAU {level}", { level: info.level })}">
      <div class="player-rank"><small>${tr("NIVEAU")}</small><b>${info.level}</b></div>
      <div class="player-xp"><strong>${intlNumber(profile.xp)}<small> XP</small></strong><div class="player-xp-track" role="progressbar" aria-label="${tr("NIVEAU {level}", { level: info.level + 1 })}" aria-valuemin="0" aria-valuemax="${info.needed}" aria-valuenow="${info.current}"><i style="width:${info.ratio * 100}%"></i></div><span>${intlNumber(info.current)} / ${intlNumber(info.needed)} XP <b>${icon("arrow", 12)} ${info.level + 1}</b></span></div>
    </section>
    <dl class="player-stats">${[
      ["target", profile.hits, "SYMBOLES"],
      ["flame", profile.bestStreak, "SÉRIE MAX"],
      ["trophy", profile.victories, "VICTOIRES"],
    ].map(([glyph, value, label]) => `<div><dt>${icon(String(glyph), 23)}<span>${tr(String(label))}</span></dt><dd>${intlNumber(Number(value))}</dd></div>`).join("")}</dl>
    <div class="collection-heading"><h3>${tr("BADGES")}</h3><span>${unlocked}<small> / ${all.length}</small></span></div>
    <div class="stamp-collection">${all.map((b) => `<article class="achievement-stamp ${b.unlocked ? "earned" : "locked"}" aria-label="${tr(b.name)}. ${tr(b.description)} ${b.current} / ${b.target}" title="${tr(b.description)}">${badgeGlyph(b.id, 68)}<strong>${tr(b.name)}</strong><span class="stamp-progress">${b.unlocked ? icon("check", 13) : ""}${b.current}<small> / ${b.target}</small></span><i class="stamp-track" aria-hidden="true"><u style="width:${b.current / b.target * 100}%"></u></i></article>`).join("")}</div>`;
  profileDirty = false;
}
function render() {
  document.body.classList.toggle(
    "is-playing",
    (view === "room" && room?.status === "playing") ||
      (view === "practice" && !!practice?.own),
  );
  document.body.dataset.view = view;
  document.body.classList.toggle("is-solo", view === "practice");
  for (const section of document.querySelectorAll<HTMLElement>(".view"))
    section.hidden = section.id !== `view-${view}`;
  homeDemo?.setActive(view === "home");
  tutorial?.setActive(view === "rules");
  library?.setActive(view === "collections");
  for (const button of document.querySelectorAll<HTMLElement>(".nav-item")) {
    const selected =
      button.dataset.view === view ||
      (button.dataset.view === "home" &&
        (view === "room" || view === "practice"));
    button.classList.toggle("active", selected);
    if (selected) button.setAttribute("aria-current", "page");
    else button.removeAttribute("aria-current");
  }
  $(".connection").classList.toggle("is-online", online);
  $("#connection-label").textContent = t(
    online ? "SERVEUR PRÊT" : "RECONNEXION…",
  );
  $("#sound-toggle").innerHTML = icon(isSoundEnabled() ? "sound" : "mute");
  $("#sound-toggle").setAttribute(
    "aria-label",
    t(isSoundEnabled() ? "Couper le son" : "Activer le son"),
  );
  $("#sound-toggle").setAttribute("aria-pressed", String(isSoundEnabled()));
  if (profileDirty) renderProfile();
  if (view === "studio" && studioDirty) {
    renderStudio();
    studioDirty = false;
  }
  if (view === "room" && roomDirty) {
    renderRoom();
    roomDirty = false;
  }
  if (view === "practice" && practiceDirty) {
    renderPractice();
    practiceDirty = false;
  }
  for (const button of document.querySelectorAll<HTMLButtonElement>(
    '[data-action="start"],[data-action="restart"]',
  )) {
    button.disabled =
      actionPending ||
      !online ||
      !!room?.players.some(player => player.connected && player.assetsReady === false) ||
      (button.dataset.action === "start" &&
        (room?.players.filter((p) => p.connected).length ?? 0) < 2);
  }
  for (const button of document.querySelectorAll<HTMLElement>(
    "#room-content .card-symbol",
  ))
    button.setAttribute("aria-disabled", String(actionPending || !online || performance.now() < roomStartsAt));
  const roundKey = view === "practice" && practice?.own
    ? `solo:${practiceRound}`
    : view === "room" && room?.status === "playing"
      ? `room:${room.code}:${room.startsAt}`
      : null;
  const deadline = view === "practice" ? practiceStartsAt : roomStartsAt;
  const waiting = roundKey !== null && performance.now() < deadline;
  document.body.classList.toggle("is-counting-down", waiting);
  for (const arena of document.querySelectorAll<HTMLElement>(".match-stage, .solo-arena"))
    arena.inert = waiting;
  countdown.sync(roundKey, deadline);
  if (isRoundActive()) {
    clearTimeout(toastTimer);
    $("#toast").hidden = true;
  }
  renderRoundFeedback();
}
function renderStudio() {
  const plan = getPrintPlan(printOrder, cardsPerPage, printSizeMode);
  previewPage = Math.max(0, Math.min(previewPage, plan.pageCount - 1));
  $("#print-summary").innerHTML =
    `<div><strong>${plan.cardCount}</strong><span>${tr("CARTES")}</span></div><div><strong>${plan.pageCount}</strong><span>${tr("FEUILLES")}</span></div><div><strong>${plan.diameterMm}<small> mm</small></strong><span>${tr("DIAMÈTRE")}</span></div>`;
  $("#preview-counter").textContent = `${previewPage + 1} / ${plan.pageCount}`;
  $<HTMLButtonElement>("#preview-prev").disabled = previewPage === 0;
  $<HTMLButtonElement>("#preview-next").disabled =
    previewPage === plan.pageCount - 1;
  const paper = $("#print-paper");
  paper.replaceChildren();
  const deck = generateDeck(printOrder);
  const collection = activeCollection();
  const slots = printFace === "back" ? getDuplexSlots(plan, previewPage) : plan.pages[previewPage];
  for (const printSlot of slots) {
    const slot = document.createElement("button");
    slot.type = "button";
    slot.dataset.previewCard = String(printSlot.cardIndex);
    slot.setAttribute("aria-label", t("Voir la carte {number} en grand", { number: printSlot.cardIndex + 1 }));
    slot.className = "paper-card-slot";
    slot.style.left = `${(printSlot.xMm / plan.pageWidthMm) * 100}%`;
    slot.style.top = `${(printSlot.yMm / plan.pageHeightMm) * 100}%`;
    slot.style.width = `${(plan.diameterMm / plan.pageWidthMm) * 100}%`;
    slot.append(printFace === "back" ? createCardBack(collection?.back ?? DEFAULT_CARD_BACK) : createCard(deck[printSlot.cardIndex], { collection }));
    paper.append(slot);
  }
  const footer = document.createElement("div");
  footer.className = "paper-footer";
  footer.textContent = `VIROU! · Ø ${plan.diameterMm} MM · ${previewPage + 1} / ${plan.pageCount}`;
  paper.append(footer);
  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-print-face]")) {
    const selected = button.dataset.printFace === printFace;
    button.classList.toggle("selected", selected);
    button.setAttribute("aria-pressed", String(selected));
  }
  for (const button of document.querySelectorAll<HTMLElement>(
    "[data-density]",
  )) {
    const selected = Number(button.dataset.density) === cardsPerPage;
    button.classList.toggle("selected", selected);
    button.setAttribute("aria-pressed", String(selected));
    button.setAttribute(
      "aria-label",
      t(
        button.dataset.density === "1"
          ? "{count} carte par page"
          : "{count} cartes par page",
        { count: button.dataset.density! },
      ),
    );
  }
}
function openCardPreview(index = previewPage * cardsPerPage) {
  const plan = getPrintPlan(printOrder, cardsPerPage, printSizeMode);
  const collection = activeCollection();
  cardPreview.open(generateDeck(printOrder), index, plan.diameterMm, collection, collection?.back ?? DEFAULT_CARD_BACK, printFace);
}
function rememberedFocus() {
  const el = document.activeElement as HTMLElement | null;
  return { symbol: el?.dataset.symbolId, slot: el?.closest(".card-face")?.id };
}
function restoreFocus(focus: ReturnType<typeof rememberedFocus>) {
  if (!focus.symbol || !focus.slot) return;
  const slot = document.getElementById(focus.slot);
  (
    slot?.querySelector<HTMLElement>(
      `button[data-symbol-id="${focus.symbol}"]`,
    ) ?? slot?.querySelector<HTMLElement>("button")
  )?.focus({ preventScroll: true });
}
function mountCards(
  center: GameCard | null,
  own: GameCard | null,
  centerSlot: string,
  ownSlot: string,
  onSymbol: (id: number) => void,
  interactive = true,
  collection: CardCollection | null = null,
) {
  if (center) {
    const card = createCard(center, {
      collection,
      interactive,
      onSymbol,
      label: t("Carte du centre : touche le symbole commun"),
    });
    card.classList.toggle("deal-in", lastCenterId !== center.id);
    $(`#${centerSlot}`).append(card);
    const back = createCardBack(collection?.back ?? DEFAULT_CARD_BACK);
    back.classList.add("countdown-back");
    back.setAttribute("aria-hidden", "true");
    $(`#${centerSlot}`).append(back);
  }
  if (own) {
    const card = createCard(own, {
      collection,
      interactive,
      onSymbol,
      label: t("Ta carte : touche le symbole commun"),
    });
    card.classList.toggle("deal-in", lastOwnId !== own.id);
    $(`#${ownSlot}`).append(card);
    const back = createCardBack(collection?.back ?? DEFAULT_CARD_BACK);
    back.classList.add("countdown-back");
    back.setAttribute("aria-hidden", "true");
    $(`#${ownSlot}`).append(back);
  }
  lastCenterId = center?.id ?? null;
  lastOwnId = own?.id ?? null;
}
function roomImagesPanel() {
  if (!room?.collection) return "";
  return `<div class="room-images-panel"><strong>${escape(room.collection.name)}</strong>${roomCollectionLoading ? `<span role="status">${tr("Chargement des images du salon…")}</span>` : roomCollectionError ? `<span role="alert">${escape(serverMessage(roomCollectionError))}</span><button class="button secondary" data-action="retry-room-images">${tr("Réessayer")}</button>` : roomCollection ? `<button class="button secondary" data-action="save-room-game">${icon("download", 18)}${tr("Enregistrer ce jeu")}</button>` : ""}</div>`;
}
function miniRoster(r: PublicRoom) {
  return `<div class="players-strip" aria-label="${tr("Cartes restantes des joueurs")}">${r.players.map((p, i) => `<div class="player-chip ${p.id === session?.playerId ? "is-me" : ""} ${p.connected ? "" : "away"}" data-player-id="${escape(p.id)}">${avatar(p.name, i)}<div><strong>${escape(p.name)}${p.id === session?.playerId ? ` <small>${tr("TOI")}</small>` : ""}</strong><span class="mini-stack" style="--mini-depth:${Math.min(p.remaining, 8)}" aria-hidden="true"></span></div><b data-player-count>${p.remaining}</b></div>`).join("")}</div>`;
}
function playToolbar(solo: boolean, completed: number, total: number) {
  const progress = Math.max(0, Math.min(100, completed / Math.max(1, total) * 100));
  return `<header class="play-toolbar ${solo ? "solo-hud" : "game-hud"}">
    <button class="icon-button" ${solo ? 'data-view="home"' : 'data-action="leave"'} aria-label="${tr(solo ? "Quitter l’entraînement" : "Quitter la partie")}">${icon("back", 21)}</button>
    <div class="play-round-info">
      <div class="play-round-topline" data-round-idle><span class="play-mode">${icon("mark", 15)}${tr(solo ? "SOLO" : "LE PUITS")}</span><span class="streak-badge ${streak >= 3 ? "on-fire" : ""}" aria-label="${tr("Série ×{count}", { count: streak })}">${icon("flame", 14)}<b>×${streak}</b></span></div>
      <span data-round-status aria-hidden="true" hidden></span>
      <div class="play-progress" role="progressbar" aria-label="${tr("Progression de la manche")}" aria-valuemin="0" aria-valuemax="${total}" aria-valuenow="${completed}"><i style="width:${progress}%"></i></div>
    </div>
    <span class="play-xp" data-round-score aria-label="${tr("{xp} XP gagnés", { xp: roundXp })}">+${roundXp}<small>XP</small></span>
    <button class="icon-button" data-action="game-sound" aria-label="${tr(isSoundEnabled() ? "Couper le son" : "Activer le son")}" aria-pressed="${isSoundEnabled()}">${icon(isSoundEnabled() ? "sound" : "mute", 20)}</button>
  </header>`;
}
function gameBoard(remaining: number, initialCount: number, centerCount: number, roster: string) {
  return `${playToolbar(false, initialCount - remaining, initialCount)}${roster}
  <div class="play-table"><div class="match-stage">
    <div class="deck-block center-deck"><div class="deck-heading"><span>${tr("AU CENTRE")}</span><b aria-label="${tr("{count} cartes au centre", { count: centerCount })}">${centerCount}</b></div>${stack("center-card-slot", centerCount - 1, "center")}</div>
    <div class="deck-block own-deck"><div class="deck-heading"><span>${tr("TA PILE")}</span><b class="pile-total" aria-label="${tr("{count} cartes restantes", { count: remaining })}">${remaining}<small>/${initialCount}</small></b></div>${stack("own-card-slot", remaining - 1)}</div>
  </div></div>`;
}
function resultStats() {
  const average = reactions.length
    ? `${intlNumber(reactions.reduce((a, b) => a + b, 0) / reactions.length / 1000, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`
    : "—";
  return `<div class="result-stats"><div><strong>+${roundXp}</strong><span>${tr("XP GAGNÉS")}</span></div><div><strong>×${bestRoundStreak}</strong><span>${tr("MEILLEURE SÉRIE")}</span></div><div><strong>${average}</strong><span>${tr("RÉFLEXE MOYEN")}</span></div></div>`;
}
function soloBoard(score: number, target: number) {
  const remaining = target - score;
  return `<div class="solo-game ${streak >= 3 ? "is-streak" : ""}" data-score="${score}">
    ${playToolbar(true, score, target)}
    <div class="play-table"><div class="solo-arena">
      <div class="solo-deck solo-draw"><div class="deck-heading"><span>${tr("AU CENTRE")}</span><b aria-label="${tr("{count} cartes au centre", { count: score + 1 })}">${score + 1}</b></div>${stack("practice-center", score, "center")}</div>
      <div class="solo-deck solo-pile"><div class="deck-heading"><span>${tr("TA PILE")}</span><b class="pile-total" aria-label="${tr("{count} cartes restantes", { count: remaining })}">${remaining}<small>/${target}</small></b></div>${stack("practice-own", remaining - 1)}</div>
    </div></div>
  </div>`;
}
function renderRoom() {
  if (!room || !session) {
    setView("home");
    return;
  }
  const focus = rememberedFocus();
  const r = room;
  const me = r.players.find((p) => p.id === session!.playerId);
  const host = r.hostId === session.playerId;
  const warning = !online
    ? `<div class="network-warning" role="alert">${tr("RECONNEXION… Tes clics reprendront quand le serveur sera prêt.")}</div>`
    : "";
  if (r.status === "lobby") {
    const connected = r.players.filter((p) => p.connected).length;
    $("#room-content").innerHTML = `
      <header class="section-heading lobby-heading">${uiArt("join", 52)}<div><h1>${tr("Salon")}</h1><span>${tr("LE PUITS")}</span></div><button class="icon-button" data-action="leave" aria-label="${tr("Quitter la partie")}">${icon("close", 20)}</button></header>
      ${warning}${roomImagesPanel()}<div class="lobby-sheet">
        <div class="invitation-ticket"><span class="field-label">${tr("CODE D’INVITATION")}</span><div class="invitation-options"><button class="room-code" data-action="copy" aria-label="${tr("Copier le lien d’invitation")}"><strong>${escape(r.code)}</strong><span>${icon("link", 18)} ${tr("COPIER LE LIEN")}</span></button><div class="invitation-qr"><div id="room-qr"></div><small>${tr("Scanne pour rejoindre")}</small></div></div></div>
        <div class="lobby-roster-heading"><h2>${tr("JOUEURS")}</h2><b>${connected}<small> / 8</small></b></div>
        <div class="lobby-roster">${r.players.map((p, i) => `<div class="lobby-player ${p.connected ? "" : "away"}">${avatar(p.name, i)}<strong>${escape(p.name)}${p.id === session!.playerId ? `<small>${tr("TOI")}</small>` : ""}</strong><span class="lobby-player-status"><i></i>${tr(!p.connected ? "RECONNEXION" : !p.assetsReady ? "Chargement des images…" : p.id === r.hostId ? "HÔTE" : "PRÊT")}</span></div>`).join("")}</div>
        <div class="lobby-rule">${uiArt("stack", 44)}<div><strong>${tr("Le premier à vider sa pile gagne.")}</strong><span>${tr("Une carte au centre. Les autres sont distribuées.")}</span><small>${tr("{cards} cartes · {symbols} symboles", { cards: getDeckStats(r.order).cards, symbols: getDeckStats(r.order).symbolsPerCard })}</small></div></div>
        <div class="lobby-start">${host ? `<button class="button primary full-width" data-action="start">${uiArt("play", 32)} ${tr("COMMENCER")}</button><p>${tr(connected < 2 ? "2 joueurs minimum" : "Tout le monde joue en même temps.")}</p>` : `<p>${tr("En attente de l’hôte")}</p>`}</div>
      </div>`;
    void renderInvitationQr($("#room-qr"), r.code);
  } else if (r.status === "finished") {
    const sorted = [...r.players].sort((a, b) => Number(r.winners.includes(b.id)) - Number(r.winners.includes(a.id)) || a.remaining - b.remaining);
    const won = r.winners.includes(session.playerId);
    const names = r.players
      .filter((p) => r.winners.includes(p.id))
      .map((p) => p.name)
      .join(" & ") || t("Un joueur");
    $("#room-content").innerHTML =
      `<div class="results-screen"><span class="result-trophy">${icon(won ? "trophy" : "star", 74)}</span><span class="eyebrow">${tr("MANCHE TERMINÉE")}</span><h1>${tr(won && me?.remaining === 0 ? "PILE VIDE !" : won ? "VICTOIRE !" : "MANCHE TERMINÉE")}</h1><p>${tr(r.winners.length > 1 ? "{names} gagnent la manche." : "{names} gagne la manche.", { names })}</p><div class="podium">${sorted.map((p, i) => `<div class="podium-player ${p.id === session!.playerId ? "is-me" : ""}"><span class="rank">${i + 1}</span>${avatar(p.name, i)}<strong>${escape(p.name)}</strong><div class="podium-stack" style="--podium-height:${Math.max(18, (p.score / Math.max(1, p.initialCount)) * 90)}px"><b>${p.remaining}</b><small>${tr("RESTANTES")}</small></div></div>`).join("")}</div>${resultStats()}${warning}${roomImagesPanel()}<div class="result-actions">${host ? `<button class="button primary" data-action="restart">${icon("play", 22)} ${tr("REVANCHE !")}</button>` : `<p>${tr("L’hôte peut relancer une manche.")}</p>`}<button class="button secondary" data-action="leave">${tr("RETOUR AU SALON")}</button></div></div>`;
  } else if (r.collection && !roomCollection) {
    $("#room-content").innerHTML = `${warning}${roomImagesPanel()}<button class="button secondary" data-action="leave">${tr("Quitter la partie")}</button>`;
  } else {
    $("#room-content").innerHTML =
      `<h1 class="sr-only">${tr("Partie multijoueur")}</h1>${warning}${gameBoard(me?.remaining ?? 0, me?.initialCount ?? 0, r.centerCount, miniRoster(r))}`;
    mountCards(
      r.centerCard,
      me?.card ?? null,
      "center-card-slot",
      "own-card-slot",
      (id) => claim(id, r.revision),
      online,
      roomCollection,
    );
  }
  restoreFocus(focus);
}
function refreshPracticeSelection() {
  const cardCount = activeCollection() ? collectionSymbolCount(activeCollection()!.order) : 57;
  const lengths = PRACTICE_LENGTHS.filter(length => length === "original" || Number(length) < cardCount);
  if (!lengths.includes(practiceLength)) practiceLength = "original";
  const target = practiceTarget(practiceLength, cardCount);
  $("#practice-length-label").textContent = t("{count} cartes", { count: target });
  $("#practice-lengths").innerHTML = lengths.map((length) => {
    const count = practiceTarget(length, cardCount);
    return `<button class="solo-length ${length === practiceLength ? "selected" : ""}" data-practice-length="${length}" aria-pressed="${length === practiceLength}">${length === "original" ? `<strong>${tr("ORIGINAL")}</strong>` : icon("stack", 24)}<b>${count}</b><small>${tr("CARTES")}</small></button>`;
  }).join("");
}
function openPracticeSetup() {
  refreshPracticeSelection();
  $<HTMLDialogElement>("#practice-dialog").showModal();
  $("#practice-lengths .selected").focus();
}
function startPractice() {
  closeDialogs();
  countdown.cancel();
  const selected = activeCollection();
  practiceCollection = selected ? toPlayableCollection(selected) : null;
  const deck = shuffledDeck(selected?.order ?? 7, crypto.getRandomValues(new Uint32Array(1))[0]);
  resetRound();
  practiceRound++;
  practiceStartsAt = performance.now() + START_COUNTDOWN_MS;
  practice = createPractice(deck, practiceLength);
  practiceDirty = true;
  setView("practice");
}
function practiceClaim(id: number) {
  if (view !== "practice" || !practice?.center || !practice.own || performance.now() < practiceStartsAt) return;
  const outcome = claimPractice(practice, id);
  if (outcome === "wrong" || outcome === "finished") return;
  rewardHit();
  shownAt = performance.now();
  if (outcome === "complete") {
    applyReward(recordPracticeComplete(currentProgress()));
    celebrateVictory({ compact: true });
    roundFeedback.clear();
  }
  practiceDirty = true;
  scheduleRender();
}
function renderPractice() {
  if (!practice) return;
  const p = practice;
  const focus = rememberedFocus();
  if (p.center && p.own) {
    $("#practice-content").innerHTML =
      `<h1 class="sr-only">${tr("Entraînement solo")}</h1>${soloBoard(p.score, p.target)}`;
    mountCards(
      p.center,
      p.own,
      "practice-center",
      "practice-own",
      practiceClaim,
      true,
      practiceCollection,
    );
  } else {
    const stars = 3;
    const average = reactions.length
      ? intlNumber(reactions.reduce((a, b) => a + b, 0) / reactions.length / 1000, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
      : "—";
    $("#practice-content").innerHTML =
      `<div class="solo-results"><button class="icon-button solo-result-exit" data-view="home" aria-label="${tr("RETOUR AU SALON")}">${icon("home", 24)}</button>
      <div class="solo-stars" role="img" aria-label="${tr("{count} étoiles sur 3", { count: stars })}">${Array.from({ length: 3 }, (_, i) => `<span class="${i < stars ? "earned" : ""}" style="--star:${i}">${icon("star", i === 1 ? 72 : 56)}</span>`).join("")}</div>
      <div class="solo-medal">${icon("stack", 42)}<strong>${p.score}<small>/${p.target}</small></strong></div>
      <h1>${tr("PILE VIDE !")}</h1><div class="solo-reward">+${roundXp}<span>XP</span></div>
      <div class="solo-result-stats"><span aria-label="${tr("MEILLEURE SÉRIE")}" title="${tr("MEILLEURE SÉRIE")}">${icon("flame", 24)} ×${bestRoundStreak}</span><span aria-label="${tr("RÉFLEXE MOYEN")}" title="${tr("RÉFLEXE MOYEN")}">${icon("target", 24)} ${average} s</span></div>
      <p class="solo-completion-count">${tr("{count} cartes posées.", { count: p.score })}</p>
      <div class="result-actions"><button class="button primary" data-action="practice">${icon("replay", 26)} ${tr("ENCORE UN TOUR")}</button><button class="button secondary" data-action="open-create">${icon("users", 24)} ${tr("JOUER EN MULTI")}</button></div></div>`;
  }
  restoreFocus(focus);
}

initEffects();
document.addEventListener("click", (event) => {
  const target = event.target as HTMLElement;
  const nav = target.closest<HTMLElement>("button[data-view]");
  if (nav) setView(nav.dataset.view as View);
  if (target.closest("[data-close-dialog]")) target.closest("dialog")?.close();
  const density = target.closest<HTMLElement>("[data-density]");
  if (density && !exporting) {
    cardsPerPage = Number(density.dataset.density) as 1 | 2 | 4;
    previewPage = 0;
    studioDirty = true;
    scheduleRender();
  }
  const face = target.closest<HTMLElement>("[data-print-face]")?.dataset.printFace;
  if (face === "front" || face === "back") {
    printFace = face;
    studioDirty = true;
    scheduleRender();
  }
  const action = target.closest<HTMLElement>("[data-action]")?.dataset.action;
  if (action === "retry-room-images" && room) {
    roomCollectionId = null;
    ensureRoomCollection(room);
    roomDirty = true;
    scheduleRender();
  }
  if (action === "save-room-game" && roomCollection) {
    try {
      const now = Date.now();
      collectionStore.getState().upsertCollection({ ...roomCollection, id: newCollectionId(), createdAt: now, updatedAt: now });
      toast("Jeu ajouté à ta bibliothèque.");
    } catch (error) { toast(error instanceof Error ? error.message : "Impossible de modifier ce jeu.", true); }
  }
  const length = target.closest<HTMLElement>("[data-practice-length]")?.dataset.practiceLength as PracticeLength | undefined;
  if (length && PRACTICE_LENGTHS.includes(length)) {
    practiceLength = length;
    savePracticeLength(length);
    refreshPracticeSelection();
    $<HTMLElement>(`[data-practice-length="${length}"]`).focus();
  }
  if (action === "open-create") openDialog("create");
  if (action === "open-join") openDialog("join");
  if (action === "nickname-reroll") setNickname(generateNickname(getLocale(), nickname));
  if (action === "start") roomAction("game:start");
  if (action === "restart") roomAction("game:restart");
  if (action === "practice") openPracticeSetup();
  if (action === "practice-start") startPractice();
  if (action === "game-sound") {
    toggleSound();
    roomDirty = true;
    practiceDirty = true;
    scheduleRender();
  }
  if (action === "leave") $<HTMLDialogElement>("#leave-dialog").showModal();
  if (action === "profile") $<HTMLDialogElement>("#profile-dialog").showModal();
  if (action === "preview-cards") openCardPreview();
  const selectedCard = target.closest<HTMLElement>("[data-preview-card]");
  if (selectedCard) openCardPreview(Number(selectedCard.dataset.previewCard));
  if (action === "copy" && room) {
    const code = room.code;
    const url = invitationUrl(code);
    if (navigator.clipboard)
      navigator.clipboard
        .writeText(url)
        .then(() => toast("LIEN COPIÉ · Fais tourner à ta team !"))
        .catch(() => toast(t("CODE : {code}", { code })));
    else toast(t("CODE : {code}", { code }));
  }
});
function bindControls() {
  for (const select of document.querySelectorAll<HTMLSelectElement>("[data-collection-select]"))
    select.addEventListener("change", () => collectionStore.getState().selectCollection(select.value || null));
  $<HTMLSelectElement>("#print-sides").addEventListener("change", event => {
    printSides = (event.target as HTMLSelectElement).value as typeof printSides;
    printFace = printSides === "back" ? "back" : "front";
    studioDirty = true;
    scheduleRender();
  });
  for (const id of ["create-name", "join-name"])
    $<HTMLInputElement>(`#${id}`).addEventListener("change", (event) =>
      setNickname((event.target as HTMLInputElement).value),
    );
  $(".brand").addEventListener("click", (event) => {
    event.preventDefault();
    setView("home");
  });
  $("#profile-button").addEventListener("click", () => {
    $<HTMLDialogElement>("#profile-dialog").showModal();
  });
  $("#sound-toggle").addEventListener("click", () => {
    const enabled = toggleSound();
    toast(enabled ? "SON ACTIVÉ · Ça va cliquer !" : "SON COUPÉ");
    scheduleRender();
  });
  $("#create-form").addEventListener("submit", (event) => {
    event.preventDefault();
    submitRoom("create");
  });
  $("#join-form").addEventListener("submit", (event) => {
    event.preventDefault();
    submitRoom("join");
  });
  $("#try-practice").addEventListener("click", openPracticeSetup);
  $("#preview-selected-game").addEventListener("click", () => library?.preview(activeCollection()));
  $("#rules-practice").addEventListener("click", openPracticeSetup);
  $("#cancel-leave").addEventListener("click", () =>
    $<HTMLDialogElement>("#leave-dialog").close(),
  );
  $("#confirm-leave").addEventListener("click", () => {
    if (!online) {
      toast("La connexion doit revenir avant de quitter.", true);
      return;
    }
    socket.timeout(8000).emit("room:leave", {}, (err, result) => {
      if (err) {
        toast("Le serveur ne répond pas.", true);
        return;
      }
      if (!result.ok) {
        toast(result.error, true);
        return;
      }
      saveSession(null);
      room = null;
      roomCollection = null;
      roomCollectionId = null;
      roomReadyEpoch++;
      roomReadyPending = false;
      roomCollectionLoading = false;
      roomCollectionError = "";
      closeDialogs();
      setView("home");
    });
  });
  $<HTMLSelectElement>("#print-order").addEventListener("change", (event) => {
    printOrder = Number((event.target as HTMLSelectElement).value) as DeckOrder;
    previewPage = 0;
    studioDirty = true;
    scheduleRender();
  });
  $<HTMLSelectElement>("#print-size").addEventListener("change", (event) => {
    printSizeMode = (event.target as HTMLSelectElement).value as
      "original" | "large";
    studioDirty = true;
    scheduleRender();
  });
  $("#preview-prev").addEventListener("click", () => {
    previewPage--;
    studioDirty = true;
    scheduleRender();
  });
  $("#preview-next").addEventListener("click", () => {
    previewPage++;
    studioDirty = true;
    scheduleRender();
  });
  $("#export-pdf").addEventListener("click", async () => {
    if (exporting) return;
    const selected = activeCollection();
    const exportOptions = {
      order: printOrder,
      cardsPerPage,
      sizeMode: printSizeMode,
      collection: selected ? toPlayableCollection(selected) : null,
      back: selected?.back ?? DEFAULT_CARD_BACK,
      sides: printSides,
    };
    exporting = true;
    const controls = document.querySelectorAll<
      HTMLButtonElement | HTMLSelectElement
    >("#export-pdf,#print-order,#print-size,#print-sides,[data-density],[data-collection-select]");
    for (const control of controls) control.disabled = true;
    $("#export-status").textContent = t("Préparation de ta pile…");
    try {
      const { exportDeckPdf } = await import("./print");
      await exportDeckPdf({
        ...exportOptions,
        onProgress: (current, total) => {
          $("#export-status").textContent = t(
            "Création des cartes : {current} / {total}",
            { current, total },
          );
        },
      });
      $("#export-status").textContent = t(
        "Ta pile est téléchargée. À tes ciseaux !",
      );
      toast("PDF PRÊT · Le jeu sort de l’écran.");
    } catch (error) {
      $("#export-status").textContent = t(
        "L’export a échoué. Tu peux réessayer.",
      );
      toast(
        error instanceof Error
          ? error.message
          : "Impossible de préparer le PDF.",
        true,
      );
    } finally {
      exporting = false;
      for (const control of controls) control.disabled = false;
      refreshCollectionControls();
    }
  });

  $("#locale-select").addEventListener("change", (event) =>
    changeLocale((event.target as HTMLSelectElement).value as Locale),
  );
}
window.addEventListener("storage", (event) => {
  if (event.key === "pile-progress-v1") {
    profile = loadProgress();
    profileDirty = true;
    scheduleRender();
  }
});
function decorateShell() {
  library?.destroy();
  library = createCollectionLibrary($("#collections-content"), {
    play(id, solo) {
      collectionStore.getState().selectCollection(id);
      if (solo) openPracticeSetup(); else openDialog("create");
    },
    print(id) {
      collectionStore.getState().selectCollection(id);
      setView("studio");
    },
    notify: toast,
  });
  library.setActive(view === "collections");
  cardPreview = createCardPreview($<HTMLDialogElement>("#card-preview-dialog"), (index, side) => {
    enlargedCardIndex = index;
    printFace = side;
    previewPage = Math.floor(index / cardsPerPage);
    studioDirty = true;
    scheduleRender();
  });
  for (const id of ["create-name", "join-name"])
    $<HTMLInputElement>(`#${id}`).value = nickname;
  refreshPracticeSelection();
  $<HTMLSelectElement>("#locale-select").value = getLocale();
  $("#locale-display").innerHTML =
    getLocale() === "pt-BR" ? "<i>🇧🇷</i><b>PT-BR</b>" : "<i>🇫🇷</i><b>FR</b>";
  $<HTMLSelectElement>("#print-order").value = String(printOrder);
  $<HTMLSelectElement>("#print-size").value = printSizeMode;
  $<HTMLSelectElement>("#print-sides").value = printSides;
  refreshCollectionControls();
  document.title = t("Virou! — À chaque image, un nouveau jeu.");
  homeDemo?.destroy();
  homeDemo = createHomeDemo($("#home-demo"), activeCollection());
  homeDemo.setActive(view === "home");
  tutorial?.destroy();
  tutorial = createTutorial($("#tutorial-demo"));
  tutorial.setActive(view === "rules");
}
function changeLocale(locale: Locale) {
  if (exporting) {
    toast("Attends la fin de l’export avant de changer de langue.");
    $<HTMLSelectElement>("#locale-select").value = getLocale();
    $("#locale-display").innerHTML =
      getLocale() === "pt-BR" ? "<i>🇧🇷</i><b>PT-BR</b>" : "<i>🇫🇷</i><b>FR</b>";
    return;
  }
  const values = new Map<string, string>();
  for (const id of ["create-name", "join-name", "join-code", "game-order"])
    values.set(id, $<HTMLInputElement>(`#${id}`).value);
  const openDialog = document.querySelector("dialog[open]")?.id;
  setLocale(locale);
  app.innerHTML = getShell();
  decorateShell();
  bindControls();
  for (const [id, value] of values) $<HTMLInputElement>(`#${id}`).value = value;
  studioDirty = true;
  roomDirty = true;
  practiceDirty = true;
  profileDirty = true;
  render();
  if (openDialog === "card-preview-dialog") openCardPreview(enlargedCardIndex);
  else if (openDialog) $<HTMLDialogElement>(`#${openDialog}`).showModal();
}
decorateShell();
bindControls();
collectionStore.subscribe((state, previous) => {
  const selected = activeCollection();
  const previousSelected = previous.collections.find(game => game.id === previous.selectedId);
  if (state.selectedId !== previous.selectedId || selected?.updatedAt !== previousSelected?.updatedAt) {
    printOrder = selected?.order ?? 7;
    previewPage = 0;
    homeDemo?.destroy();
    homeDemo = createHomeDemo($("#home-demo"), selected);
    homeDemo.setActive(view === "home");
    studioDirty = true;
  }
  refreshCollectionControls();
  refreshPracticeSelection();
  scheduleRender();
});
// A failed background refresh leaves the previous local edition usable.
void refreshInstalledClassic().catch(() => {});
const inviteCode = new URLSearchParams(location.search).get("room");
if (inviteCode) {
  $<HTMLInputElement>("#join-code").value = inviteCode
    .toUpperCase()
    .slice(0, 6);
  openDialog("join");
}
scheduleRender();
